import React, {FC, SyntheticEvent, useCallback, useLayoutEffect, useState} from "react";
import './HardCosts.less';
import {FormControl} from "@mui/material";
import DatePicker from "../../../ui/DateComponents/DatePicker/DatePicker";
import {CampaignHardCosts} from "../../../entities/campaign";
import {EditableTableCardProps} from "../../../ui/EditableTable/EditableTable";
import CurrencyInput from "../../../ui/Inputs/CurrencyInput";
import OkCancel from "../../../ui/Buttons/OkCancel";
import failureLadder from "../../../lib/failureLadder";
import {genUUID} from "../../../lib/picos/uuids";
import useComponentCache from "../../../hooks/useComponentCache";

export type HardCostsCardProps = EditableTableCardProps<CampaignHardCosts>;

type HardCostsCardState = {
    current: CampaignHardCosts,
}

const HardCostsCard: FC<HardCostsCardProps> = props => {
    const {cacheSave, cacheReset, cacheValue} = useComponentCache<HardCostsCardState>(JSON.stringify(props.current || 'hard-costs-card'));

    const [current, setCurrent] = useState<CampaignHardCosts>(cacheValue('current', props.current ?? {id: genUUID(), item: '', cost: 0, incurredOn: new Date().toUTCString()}));

    // Save state
    useLayoutEffect(() => {
        return () => cacheSave({current});
    }, [cacheSave, current]);

    const a = current.item === '';
    const b = current.cost === 0;

    const handleIncurredChange = useCallback((date: Date) => {
        setCurrent({...current, incurredOn: date.toUTCString()});
    }, [current, setCurrent]);

    const handleItemBlur = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrent({...current, item: e.target.value});
    }, [current, setCurrent]);

    const handleCostBlur = useCallback((cost: number) => {
        setCurrent({...current, cost});
    }, [current, setCurrent]);

    const onOk = (e: SyntheticEvent) => {
        e.preventDefault();

        cacheReset();
        props.onOk(current);
    }

    const onCancel = (e: SyntheticEvent) => {
        e.preventDefault();

        cacheReset();
        props.onCancel();
    }

    const disabled = current.cost <= 0 && current.item.trim() === '' && current.incurredOn === '';

    const failureReasons = failureLadder([
        [current.cost <= 0, 'Cost must be greater than zero'],
        [current.item.trim() === '', 'Item cannot be blank'],
        [current.incurredOn === '', 'Date cannot be blank'],
    ]);

    return <div className='hard-costs__line Form'>
        {/* Incurred Date */}
        <FormControl variant='standard' sx={{display: 'flex'}}
        >
            <label htmlFor={`hardCosts-${current.id}-IncurredOn`}>Incurred On</label>
            <div id={`hardCosts-${current.id}-IncurredOn`}>
            <DatePicker name='hardCosts'
                        defaultValue={current.incurredOn}
                        autoFocus={a && b}
                        useTime={false}
                        onChange={handleIncurredChange}
            />
            </div>
        </FormControl>

        {/* Item Name */}
        <FormControl id={`hardCosts-${current.id}-Item`}>
            <label htmlFor={`hardCosts-${current.item}-Item`}>Description</label>
            <input id={`hardCosts-${current.item}-Item`}
                   key={`hardCosts-${current.item}-Item`}
                   autoFocus={!a && b}
                   type='text'
                   defaultValue={current.item}
                   onBlur={handleItemBlur}
                   placeholder='What was done?'
                   style={{height: '2.5em' }}
            />
        </FormControl>

        {/* Per-Item Cost */}
        <FormControl id={`hardCosts-${current.id}-Cost`} sx={{width: '8em'}}>
            <label htmlFor={current.id}>Cost</label>
            <CurrencyInput id={current.id}
                           value={Number(current.cost)}
                           currency='USD'
                           useCommas={true}
                           useEuropean={false}
                           onBlur={handleCostBlur}
            />
        </FormControl>

        <OkCancel label='Hard Cost'
                  disabled={disabled}
                  disabledMessage={failureReasons}
                  onClick={onOk}
                  onSubmit={onOk}
                  onCancel={onCancel}
                  onTop={true}
        />
    </div>;
}

export default HardCostsCard;